import { useMemo } from "react";
import Tooltip from "react-bootstrap/Tooltip";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons/faQuestionCircle";
import { resources } from "@constants/hardcoded-strings";
import {
    WorkflowReportPassRateDataType,
    WorkflowReportPassRatesOverTimeResponse,
    WorkflowReportRpServiceConfiguration
} from "@idw-types/data/reports";
import { WorkflowReportSearchFiltersState } from "@idw-types/state/workflow-report-state";
import { CardContainer } from "../../../common/card-container/card-container";
import { ContentPageBody } from "../../../common/content-page-body/content-page-body";
import { ContentPageHeader } from "../../../common/content-page-header/content-page-header";
import { WorkflowReportPassRatesPieChart } from "./workflow-report-pass-rates-pie-chart/workflow-report-pass-rates-pie-chart";
import { WorkflowReportPassRatesOverTimeAreaChart } from "./workflow-report-pass-rates-over-time-area-chart/workflow-report-pass-rates-over-time-area-chart";
import styles from "./workflow-report-pass-rates-container.less";
import { WorkflowLegendContainer } from "../workflow-legend-container/workflow-legend-container";
import { WORKFLOW_REPORT_PASS_RATES_LEGEND_ITEMS } from "@constants/workflow-report";
import { LoadingIndicator } from "@containers/common/loading-indicator/loading-indicator";

type WorkflowReportPassRatesContainerProps = {
    passRatesResponse: WorkflowReportPassRateDataType;
    passRatesOverTimeResponse: WorkflowReportPassRatesOverTimeResponse;
    rpServices: Array<WorkflowReportRpServiceConfiguration>;
    searchFilters: WorkflowReportSearchFiltersState;
    isLoading: boolean;
};

export const WorkflowReportPassRatesContainer = (props: WorkflowReportPassRatesContainerProps) => {
    const {
        passRatesResponse,
        passRatesOverTimeResponse,
        rpServices,
        searchFilters,
        isLoading
    } = props;

    const { approvedCount, deniedCount, totalCount } = useMemo(() => {
        let approvedCount = 0;
        let deniedCount = 0;
        let totalCount = 0;
        if (passRatesResponse) {
            Object.keys(passRatesResponse.approved).forEach((key: any) => {
                if (!isNaN(passRatesResponse.approved[key])) {
                    approvedCount += passRatesResponse.approved[key];
                }
            });
            deniedCount = passRatesResponse.denied;
            totalCount = approvedCount + deniedCount;
        }

        return { approvedCount, deniedCount, totalCount };
    }, [passRatesResponse]);

    const passRatesTooltip = useMemo(() => {
        const renderTooltip = (props: any) => {
            return <Tooltip {...props}>{resources.pages.workflowReport.sections.passRates.tooltip}</Tooltip>
        }
        return (
            <OverlayTrigger overlay={renderTooltip} placement={"auto"}>
                <div><FontAwesomeIcon icon={faQuestionCircle} /></div>
            </OverlayTrigger>
        );
    }, []);

    return (
        <CardContainer>
            <ContentPageHeader title={resources.pages.workflowReport.sections.passRates.title} headerButton={passRatesTooltip} />
            <ContentPageBody isMaxWidth={true}>
                {isLoading && <LoadingIndicator title={resources.common.loadingIndicatorText} />}
                {(!isLoading && passRatesResponse && passRatesOverTimeResponse && rpServices) &&
                    <>
                        <span id="workflow-report-pass-rates-description" dangerouslySetInnerHTML={{ __html: resources.pages.workflowReport.sections.passRates.description(totalCount, approvedCount, deniedCount) }} />
                        {totalCount > 0 && (
                            <div className={styles.graphContainer}>
                                <div className={styles.workflowReportPassRatesPieChartContainer}>
                                    <WorkflowReportPassRatesPieChart
                                        passRatesResponse={passRatesResponse}
                                        rpServices={rpServices}
                                        searchFilters={searchFilters}
                                    />
                                    <div className={styles.workflowReportPassRatesOverTimeAreaLegend}>
                                        <WorkflowLegendContainer
                                            items={WORKFLOW_REPORT_PASS_RATES_LEGEND_ITEMS}
                                        />
                                    </div>
                                </div>
                                <div className={styles.workflowReportPassRatesOverTimeAreaChartContainer}>
                                    <WorkflowReportPassRatesOverTimeAreaChart
                                        passRatesOverTimeResponse={passRatesOverTimeResponse}
                                        rpServices={rpServices}
                                        searchFilters={searchFilters}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                }

            </ContentPageBody>
        </CardContainer>
    );
};